import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// WAT is UTC+1 with no daylight saving, so a fixed offset is enough
const WAT_OFFSET_MINUTES = 60;

// Sunset timeline for Payment Link. Times are end of day WAT (UTC+1).
export const SUNSET_DATES = {
  announced: dayjs("2026-10-06T00:00:00+01:00"),
  linksClose: dayjs("2026-11-30T23:59:00+01:00"),
  exportsEnd: dayjs("2027-01-31T23:59:00+01:00"),
};

// How close to linksClose the banner switches to the urgent (dark) variant
export const FINAL_WEEKS_THRESHOLD_DAYS = 14;

export type SunsetPhase = "announced" | "finalWeeks" | "closed" | "exportsEnded";

export const getSunsetPhase = (now: Dayjs = dayjs()): SunsetPhase => {
  if (now.isAfter(SUNSET_DATES.exportsEnd)) return "exportsEnded";
  if (now.isAfter(SUNSET_DATES.linksClose)) return "closed";
  if (daysUntil(SUNSET_DATES.linksClose, now) <= FINAL_WEEKS_THRESHOLD_DAYS) {
    return "finalWeeks";
  }
  return "announced";
};

export const daysUntil = (date: Dayjs, now: Dayjs = dayjs()) =>
  Math.max(0, Math.ceil(date.diff(now, "day", true)));

// Progress along a 3-point timeline: announced (0%) -> linksClose (50%) -> exportsEnd (100%)
export const getTimelineProgress = (now: Dayjs = dayjs()) => {
  const { announced, linksClose, exportsEnd } = SUNSET_DATES;
  const fraction = (start: Dayjs, end: Dayjs) =>
    Math.min(1, Math.max(0, now.diff(start) / end.diff(start)));

  if (now.isBefore(linksClose)) return fraction(announced, linksClose) * 50;
  return 50 + fraction(linksClose, exportsEnd) * 50;
};

// Format in WAT, not the browser's timezone, so dates match the "23:59 WAT" copy everywhere
export const formatLongDate = (date: Dayjs) =>
  date.utcOffset(WAT_OFFSET_MINUTES).format("D MMMM YYYY");
export const formatShortDate = (date: Dayjs) =>
  date.utcOffset(WAT_OFFSET_MINUTES).format("D MMM YYYY");

// Once links close, link history is read-only
export const isLinkDeletionDisabled = (now: Dayjs = dayjs()) =>
  ["closed", "exportsEnded"].includes(getSunsetPhase(now));
